import React, { useEffect, useContext } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";

import Login from "./views/Login";
import RealTime from "./views/RealTime";
import Demo from "./views/Demo";
import CannedMessages from "./views/CannedMessages";
import LMRGroups from "./views/LMRGroups";
import LMRUsers from "./views/LMRUsers";
import NewLMRUsers from "./views/NewLMRUsers";
import Locations from "./views/Locations";
import OrganizationMapping from "./views/OrganizationMapping";
import OrganizationTranslation from "./views/OrganizationTranslation";
import Organizations from "./views/Organizations";
import NewOrganization from "./views/NewOrganization";
import OtarConnections from "./views/OTARConnections";
import ESLayout from "./components/layouts/esLayout";
import XSSIConnections from "./views/XSSIConnections";
import AuthContext from "./store/authContext";
import NewXSSIConnections from "./views/NewXSSIConnection";
import MCPTTGroups from "./views/MCPTTGroups";
import NewMCPTTGroups from "./views/NewMCPTTGroups";
import MCPTTUsers from "./views/MCPTTUsers";
import NewMCPTTUsers from "./views/NewMCPTTUsers";
import NewOTARConnections from "./views/NewOTARConnections";
import Instances from "./views/Instances";
import Faults from "./views/Faults";
import Inventory from "./views/Inventory";
import HistoricalAlarms from "./views/HistoricalAlarms";
import ActiveAlarms from "./views/ActiveAlarms";
import NewLocation from "./views/NewLocation";
import NewOrganizationMapping from "./views/NewOrganizationMapping";
import {
  PERMISSION_DENIED_HEADER,
  permissionDeniedSubtext,
} from "./constants/permissionsCopy";
import InfoAlert from "./ui/InfoAlert";
import NewOrganisationTranslation from "./views/NewOrganizationTranslation";
import DFSIConnections from "./views/DFSIConnections";
import DFSIChannels from "./views/DFSIChannels";
import DFSIGroups from "./views/DFSIGroups";
import NewDFSIConnections from "./views/NewDFSIConnections";
import NewDFSIChannels from "./views/NewDFSIChannels";
import NewDFSIGroups from "./views/NewDFSIGroups";
import NewCannedMessages from "./views/NewCannedMessages";
import NewLMRGroups from "./views/NewLMRGroups";
import P25Unit from "./views/P25Unit";
import P25Group from "./views/P25Group";
import FixedNetworkElement from "./views/FixedNetworkElement";
import P25Affiliations from "./views/P25Affiliations";
import Overview from "./views/Overview";
import SSOLogin from "./views/SSOLogin";
import IKIConnections from "./views/IKIConnections";
import NewIKIConnection from "./views/NewIKIConnection";

const getComponentDetailsForname = (name: string) => {
  switch (name) {
    case "XSSI_READ":
      return {
        scopeKey: "xssi_connections",
        module: "XSSI Connections",
        component: <XSSIConnections />,
        operation: "view",
        permission: "r",
      };
    case "OTAR_READ":
      return {
        scopeKey: "otar_connections",
        module: "OTAR Connections",
        component: <OtarConnections />,
        operation: "view",
        permission: "r",
      };
    case "MC_USERS_READ":
      return {
        scopeKey: "mc_users",
        module: "MCPTT Users",
        component: <MCPTTUsers />,
        operation: "view",
        permission: "r",
      };
    case "MC_GROUPS_READ":
      return {
        scopeKey: "mc_groups",
        module: "MCPTT Groups",
        component: <MCPTTGroups />,
        operation: "view",
        permission: "r",
      };
    case "LMR_USERS_READ":
      return {
        scopeKey: "lmr_users",
        module: "LMR Users",
        component: <LMRUsers />,
        operation: "view",
        permission: "r",
      };
    case "LMR_GROUPS_READ":
      return {
        scopeKey: "lmr_groups",
        module: "LMR Groups",
        component: <LMRGroups />,
        operation: "view",
        permission: "r",
      };
    case "LMR_GROUPS_CREATE":
      return {
        scopeKey: "lmr_groups",
        module: "New LMR Groups",
        component: <NewLMRGroups />,
        operation: "create",
        permission: "c",
      };
    case "DFSI_CONNECTIONS_READ":
      return {
        scopeKey: "dfsi_connections",
        module: "DFSI Connections",
        component: <DFSIConnections />,
        operation: "view",
        permission: "r",
      };
    case "DFSI_CHANNELS_READ":
      return {
        scopeKey: "dfsi_channels",
        module: "DFSI Channels",
        component: <DFSIChannels />,
        operation: "view",
        permission: "r",
      };
    case "DFSI_GROUPS_READ":
      return {
        scopeKey: "dfsi_groups",
        module: "DFSI Groups",
        component: <DFSIGroups />,
        operation: "view",
        permission: "r",
      };
    case "DFSI_CONNECTIONS_CREATE":
      return {
        scopeKey: "dfsi_connections",
        module: "New DFSI Connections",
        component: <NewDFSIConnections />,
        operation: "create",
        permission: "c",
      };
    case "DFSI_CHANNELS_CREATE":
      return {
        scopeKey: "dfsi_channels",
        module: "New DFSI Channels",
        component: <NewDFSIChannels />,
        operation: "create",
        permission: "c",
      };
    case "DFSI_GROUPS_CREATE":
      return {
        scopeKey: "dfsi_groups",
        module: "New DFSI Groups",
        component: <NewDFSIGroups />,
        operation: "create",
        permission: "c",
      };
    case "LMR_MSGS_READ":
      return {
        scopeKey: "lmr_msgs",
        module: "Canned Messages",
        component: <CannedMessages />,
        operation: "view",
        permission: "r",
      };
    case "LMR_MSGS_CREATE":
      return {
        scopeKey: "lmr_msgs",
        module: "New Canned Message",
        component: <NewCannedMessages />,
        operation: "create",
        permission: "c",
      };
    case "LOCATIONS_READ":
      return {
        scopeKey: "locations",
        module: "Locations",
        component: <Locations />,
        operation: "view",
        permission: "r",
      };
    case "ORG_MAPPING_READ":
      return {
        scopeKey: "org_mappings",
        module: "Organisation Mappings",
        component: <OrganizationMapping />,
        operation: "view",
        permission: "r",
      };
    case "ORG_TRANSLATION_READ":
      return {
        scopeKey: "org_translations",
        module: "Organisation Translations",
        component: <OrganizationTranslation />,
        operation: "view",
        permission: "r",
      };
    case "ORG_READ":
      return {
        scopeKey: "orgs",
        module: "Organisations",
        component: <Organizations />,
        operation: "view",
        permission: "r",
      };
    case "ORG_CREATE":
      return {
        scopeKey: "orgs",
        module: "New Organisation",
        component: <NewOrganization />,
        operation: "create",
        permission: "c",
      };
    case "PUBLISH_INFO_READ":
      return {
        scopeKey: "real_time",
        module: "Publish Info",
        component: <RealTime />,
        operation: "view",
        permission: "r",
      };
    case "ALARMS_READ":
      return {
        scopeKey: "fm_alarms",
        module: "Active Alarms",
        component: <ActiveAlarms />,
        operation: "view",
        permission: "r",
      };
    case "HISTORICAL_ALARMS_READ":
      return {
        scopeKey: "fm_alarms",
        module: "Historical Alarms",
        component: <HistoricalAlarms />,
        operation: "view",
        permission: "r",
      };
    case "FAULTS_READ":
      return {
        scopeKey: "fm_alarms",
        module: "Faults",
        component: <Faults />,
        operation: "view",
        permission: "r",
      };
    case "INVENTORY_READ":
      return {
        scopeKey: "fm_alarms",
        module: "Inventory",
        component: <Inventory />,
        operation: "view",
        permission: "r",
      };
    case "INSTANCES_READ":
      return {
        scopeKey: "fm_alarms",
        module: "Instances",
        component: <Instances />,
        operation: "view",
        permission: "r",
      };
    case "XSSI_CREATE":
      return {
        scopeKey: "xssi_connections",
        module: "New XSSI Connections",
        component: <NewXSSIConnections />,
        operation: "create",
        permission: "c",
      };
    case "OTAR_CREATE":
      return {
        scopeKey: "otar_connections",
        module: "New OTAR Connections",
        component: <NewOTARConnections />,
        operation: "create",
        permission: "c",
      };
    case "MC_USERS_CREATE":
      return {
        scopeKey: "mc_users",
        module: "New MCPTT Users",
        component: <NewMCPTTUsers />,
        operation: "create",
        permission: "c",
      };
    case "MC_GROUPS_CREATE":
      return {
        scopeKey: "mc_groups",
        module: "New MCPTT Group",
        component: <NewMCPTTGroups />,
        operation: "create",
        permission: "c",
      };
    case "LMR_USERS_CREATE":
      return {
        scopeKey: "lmr_users",
        module: "New LMR Users",
        component: <NewLMRUsers />,
        operation: "create",
        permission: "c",
      };
    case "LOCATIONS_CREATE":
      return {
        scopeKey: "locations",
        module: "New Locations",
        component: <NewLocation />,
        operation: "create",
        permission: "c",
      };
    case "ORG_MAPPING_CREATE":
      return {
        scopeKey: "org_mappings",
        module: "New Organisation Mappings",
        component: <NewOrganizationMapping />,
        operation: "create",
        permission: "c",
      };
    case "ORG_TRANSLATION_CREATE":
      return {
        scopeKey: "org_translations",
        module: "New Organisation Translations",
        component: <NewOrganisationTranslation />,
        operation: "create",
        permission: "c",
      };
    case "P25_UNIT_READ":
      return {
        scopeKey: "real_time",
        module: "P25 Unit",
        component: <P25Unit />,
        operation: "view",
        permission: "r",
      };
    case "P25_GROUP_READ":
      return {
        scopeKey: "real_time",
        module: "P25 Group",
        component: <P25Group />,
        operation: "view",
        permission: "r",
      };
    case "FIXED_NETWORK_ELEMENT_READ":
      return {
        scopeKey: "real_time",
        module: "Fixed Network Element",
        component: <FixedNetworkElement />,
        operation: "view",
        permission: "r",
      };
    case "P25_AFFILIATIONS_READ":
      return {
        scopeKey: "real_time",
        module: "P25 Group Affiliation",
        component: <P25Affiliations />,
        operation: "view",
        permission: "r",
      };
    case "IKI_CONNECTIONS_CREATE":
      return {
        scopeKey: "iki_connections",
        module: "New IKI Connections",
        component: <NewIKIConnection />,
        operation: "create",
        permission: "c",
      };
    case "IKI_CONNECTION_READ":
      return {
        scopeKey: "iki_connections",
        module: "IKI Connections",
        component: <IKIConnections />,
        operation: "view",
        permission: "r",
      };
    default:
      throw new Error("Unsupported route name");
  }
};

const CheckReadPermission = ({
  name,
  isLoggedIn,
}: {
  name: string;
  isLoggedIn: boolean;
}) => {
  const { scopes } = useContext(AuthContext);
  const { scopeKey, module, component, operation, permission } =
    getComponentDetailsForname(name);

  if (!(scopes && scopes[scopeKey] && scopes[scopeKey].includes(permission))) {
    return (
      <ESLayout>
        <InfoAlert
          heading={PERMISSION_DENIED_HEADER}
          subText={permissionDeniedSubtext({ module, operation })}
        />
      </ESLayout>
    );
  }

  return (
    <ESLayout>
      {isLoggedIn ? <div className="content-wrapper">{component}</div> : null}
    </ESLayout>
  );
};

const App = () => {
  const authContext = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn } = authContext;

  // Check if running in iframe
  const isInIframe = window !== window.parent;

  useEffect(() => {
    let navigationPath = null;
    if (isInIframe) {
      if (!isLoggedIn) {
        navigationPath = "/sso";
      } else {
        navigationPath = "/overview";
      }
    } else {
      switch (location.pathname) {
        case "/login":
          if (isLoggedIn) {
            navigationPath = "/configurationManagement/organizations";
          }
          break;
        case "/":
          navigationPath = isLoggedIn
            ? "/configurationManagement/organizations"
            : "/login";
          break;
        default:
          if (!isLoggedIn) {
            navigationPath = "/login";
          }
          break;
      }
    }

    if (navigationPath) {
      navigate(navigationPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return (
    <div>
      <Routes>
        {isInIframe && <Route path="/sso" element={<SSOLogin />} />}
        <Route path="/login" element={<Login />} />
        <Route
          path="/realtime/publishinfo"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="PUBLISH_INFO_READ"
            />
          }
        />
        <Route
          path="/realtime/p25unit"
          element={
            <CheckReadPermission isLoggedIn={isLoggedIn} name="P25_UNIT_READ" />
          }
        />
        <Route
          path="/realtime/p25group"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="P25_GROUP_READ"
            />
          }
        />
        <Route
          path="/realtime/fixednetworkelement"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="FIXED_NETWORK_ELEMENT_READ"
            />
          }
        />
        <Route
          path="/realtime/p25affiliations"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="P25_AFFILIATIONS_READ"
            />
          }
        />
        <Route
          path="/demo"
          element={
            <ESLayout>
              <div className="content-wrapper">
                <Demo />
              </div>
            </ESLayout>
          }
        />
        <Route
          path="/configurationManagement/cannedmessages"
          element={
            <CheckReadPermission isLoggedIn={isLoggedIn} name="LMR_MSGS_READ" />
          }
        />
        <Route
          path="/configurationManagement/lmrgroups"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="LMR_GROUPS_READ"
            />
          }
        />
        <Route
          path="/configurationManagement/lmrusers"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="LMR_USERS_READ"
            />
          }
        />
        <Route
          path="/configurationManagement/mcpttGroups"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="MC_GROUPS_READ"
            />
          }
        />
        <Route
          path="/configurationManagement/mcpttGroups/new"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="MC_GROUPS_CREATE"
            />
          }
        />
        <Route
          path="/configurationManagement/dfsiConnections"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="DFSI_CONNECTIONS_READ"
            />
          }
        />
        <Route
          path="/configurationManagement/dfsiChannels"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="DFSI_CHANNELS_READ"
            />
          }
        />
        <Route
          path="/configurationManagement/dfsiGroups"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="DFSI_GROUPS_READ"
            />
          }
        />
        <Route
          path="/configurationManagement/dfsiConnections/new"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="DFSI_CONNECTIONS_CREATE"
            />
          }
        />
        <Route
          path="/configurationManagement/dfsiChannels/new"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="DFSI_CHANNELS_CREATE"
            />
          }
        />
        <Route
          path="/configurationManagement/dfsiGroups/new"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="DFSI_GROUPS_CREATE"
            />
          }
        />
        <Route
          path="/configurationManagement/mcpttUsers"
          element={
            <CheckReadPermission isLoggedIn={isLoggedIn} name="MC_USERS_READ" />
          }
        />
        <Route
          path="/configurationManagement/locations"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="LOCATIONS_READ"
            />
          }
        />
        <Route
          path="/configurationManagement/organizationmapping"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="ORG_MAPPING_READ"
            />
          }
        />
        <Route
          path="/configurationManagement/organizations"
          element={
            <CheckReadPermission isLoggedIn={isLoggedIn} name="ORG_READ" />
          }
        />
        <Route
          path="/configurationManagement/organizations/new"
          element={
            <CheckReadPermission isLoggedIn={isLoggedIn} name="ORG_CREATE" />
          }
        />
        <Route
          path="/configurationManagement/organizationtranslation"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="ORG_TRANSLATION_READ"
            />
          }
        />
        <Route
          path="/configurationManagement/organizationtranslation/new"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="ORG_TRANSLATION_CREATE"
            />
          }
        />
        <Route
          path="/configurationManagement/otarconnections"
          element={
            <CheckReadPermission isLoggedIn={isLoggedIn} name="OTAR_READ" />
          }
        />
        <Route
          path="/faultManagement/instances"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="INSTANCES_READ"
            />
          }
        />
        <Route
          path="/faultManagement/faults"
          element={
            <CheckReadPermission isLoggedIn={isLoggedIn} name="FAULTS_READ" />
          }
        />
        <Route
          path="/faultManagement/HistoricalAlarms"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="HISTORICAL_ALARMS_READ"
            />
          }
        />
        <Route
          path="/faultManagement/ActiveAlarms"
          element={
            <CheckReadPermission isLoggedIn={isLoggedIn} name="ALARMS_READ" />
          }
        />
        <Route
          path="/faultManagement/inventory"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="INVENTORY_READ"
            />
          }
        />
        <Route
          path="/configurationManagement/xssiconnections"
          element={
            <CheckReadPermission isLoggedIn={isLoggedIn} name="XSSI_READ" />
          }
        />
        <Route
          path="/configurationManagement/xssiconnections/new"
          element={
            <CheckReadPermission isLoggedIn={isLoggedIn} name="XSSI_CREATE" />
          }
        />
        <Route
          path="/configurationManagement/otarConnections/new"
          element={
            <CheckReadPermission isLoggedIn={isLoggedIn} name="OTAR_CREATE" />
          }
        />
        <Route
          path="/configurationManagement/locations/new"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="LOCATIONS_CREATE"
            />
          }
        />
        <Route
          path="/configurationManagement/organizationmapping/new"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="ORG_MAPPING_CREATE"
            />
          }
        />
        <Route
          path="/configurationManagement/mcpttUsers/new"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="MC_USERS_CREATE"
            />
          }
        />
        <Route
          path="/configurationManagement/lmrusers/new"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="LMR_USERS_CREATE"
            />
          }
        />
        <Route
          path="/configurationManagement/cannedMessages/new"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="LMR_MSGS_CREATE"
            />
          }
        />
        <Route
          path="/configurationManagement/lmrgroups/new"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="LMR_GROUPS_CREATE"
            />
          }
        />
        <Route
          path="/overview"
          element={
            <ESLayout>
              <div className="content-wrapper">
                <Overview />
              </div>
            </ESLayout>
          }
        />
        <Route
          path="/configurationManagement/ikiconnections"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="IKI_CONNECTION_READ"
            />
          }
        />
        <Route
          path="/configurationManagement/ikiconnections/new"
          element={
            <CheckReadPermission
              isLoggedIn={isLoggedIn}
              name="IKI_CONNECTIONS_CREATE"
            />
          }
        />
        <Route path="*" element={<h1>Not found</h1>} />
      </Routes>
    </div>
  );
};

export default App;
